import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { t } from "@lingui/macro";
import { Plus } from "@phosphor-icons/react";
import { SectionItem, SectionKey, SectionWithItem, awardSchema, sectionSchema, certificationSchema, customSectionSchema, educationSchema, experienceSchema, interestSchema, languageSchema, profileSchema, skillSchema, basicsSchema, itemSchema } from "@reactive-resume/schema";
import { Button, Tooltip } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import get from "lodash.get";

import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { getSectionIcon } from "./section-icon";
import { SectionListItem } from "./section-list-item";
import { SectionOptions } from "./section-options";
import { ZodError } from "zod";
import { useEffect, useState } from "react";

type Props<T extends SectionItem> = {
  id: SectionKey;
  title: (item: T) => string;
  description?: (item: T) => string | undefined;
  name?: any;
  tooltip?: any;
};

export const SectionBase = <T extends SectionItem>({ id, title, description, name, tooltip }: Props<T>) => {

  const { open } = useDialog(id);
  const setValue = useResumeStore((state) => state.setValue);
  const [validationErrors, setValidationErrors]: any = useState({});
  const section = useResumeStore((state) =>
    get(state.resume.data.sections, id),
  ) as SectionWithItem<T>;
  // console.log("chhhh", section)
  let a: any = {
    'profiles': profileSchema,
    'experience': experienceSchema,
    'education': educationSchema,
    'skills': skillSchema,
    'languages': languageSchema,
    'awards': awardSchema,
    'certifications': certificationSchema,
    'interests': interestSchema
  }
  let schema: any = a[id]
  // console.log(schema,id)
  useEffect(() => {
    section.items.map((a) => {
      try {
        schema.parse(a);
        setValidationErrors({});
      } catch (error: any) {

        if (error instanceof ZodError) {
          const errors = error.errors.reduce((acc: any, err) => {
            acc[err.path[0]] = err.message;
            return acc;
          }, {});
          setValidationErrors(errors);
        } else {
          console.error('Unexpected error during validation:', error.message);
        }
      }
    })
  }, [section, id]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  if (!section) return null;

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = section.items.findIndex((item) => item.id === active.id);
      const newIndex = section.items.findIndex((item) => item.id === over.id);

      const sortedList = arrayMove(section.items as T[], oldIndex, newIndex);
      setValue(`sections.${id}.items`, sortedList);
    }
  };

  const onCreate = () => open("create", { id });
  const onUpdate = (item: T) => open("update", { id, item });
  const onDuplicate = (item: T) => open("duplicate", { id, item });
  const onDelete = (item: T) => open("delete", { id, item });

  const onToggleVisibility = (index: number) => {
    const visible = get(section, `items[${index}].visible`, true);
    setValue(`sections.${id}.items[${index}].visible`, !visible);
  };

  return (
    <motion.section
      id={id}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="grid gap-y-6"
    >
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          {getSectionIcon(id)}

          <h2 className=" text-3xl font-bold">{name} <Tooltip content={tooltip}>

            <i className="fas fa-info-circle text-orange-500 i-75-cent"></i></Tooltip></h2>
        </div>

        <div className="flex items-center gap-x-2">
          <SectionOptions id={id} />
        </div>
      </header>

      <main className={cn("grid transition-opacity", !section.visible && "opacity-50")}>
        {section.items.length === 0 && (
          <Button
            variant="outline"
            onClick={onCreate}
            className="gap-x-2 border-dashed py-6 leading-relaxed hover:bg-secondary-accent"
          >
            <Plus size={14} />
            <span className="font-medium">
              {(t`Create`) + ` ` + `${section.name.endsWith('s') ? section.name.slice(0, -1) : section.name}`}
            </span>
          </Button>
        )}

        <DndContext
          sensors={sensors}
          onDragEnd={onDragEnd}
          collisionDetection={closestCenter}
          modifiers={[restrictToParentElement]}
        >
          <SortableContext items={section.items} strategy={verticalListSortingStrategy}>
            <AnimatePresence>
              {section.items.map((item, index) => (
                <SectionListItem
                  id={item.id}
                  key={item.id}
                  visible={item.visible}
                  title={title(item as T)}
                  description={description?.(item as T)}
                  onUpdate={() => onUpdate(item as T)}
                  onDelete={() => onDelete(item as T)}
                  onDuplicate={() => onDuplicate(item as T)}
                  onToggleVisibility={() => onToggleVisibility(index)}
                  hasError={!schema.safeParse(item).success}
                />
              ))}
            </AnimatePresence>
          </SortableContext>
        </DndContext>
      </main>

      {section.items.length > 0 && (
        <footer className="flex items-center justify-end">
          <Button variant="outline" className="ml-auto gap-x-2" onClick={onCreate}>
            <Plus />
            <span>
              {(t`Create`) + ` ` + `${section.name.endsWith('s') ? section.name.slice(0, -1) : section.name}`}
            </span>
          </Button>
        </footer>
      )}
    </motion.section>
  );
};
